/**
 * Flow Optimizer — API client: `/<provider>/flow-optimizer/*` for each tracker
 * (Voluum, Binom, Tres Commas); `provider` defaults to 'voluum' (back-compat).
 * For Media Devoted | Created By Ciro Autiero
 */

import api from '@/services/api';
import type {
  TrackerId,
  FlowOptimizerConfig,
  FlowOptimizerConfigListItem,
  FlowOptimizerMetadata,
  FlowOptimizerPreviewResult,
  FlowOptimizerSchedulerStatus,
  VoluumFlowUpdate,
} from '@trafficpulse/shared';

export type FlowOptimizerProvider = TrackerId;

function baseFor(provider: FlowOptimizerProvider = 'voluum'): string {
  return `/${provider}/flow-optimizer`;
}

export async function getMetadata(
  provider: FlowOptimizerProvider = 'voluum',
): Promise<FlowOptimizerMetadata> {
  const { data } = await api.get(`${baseFor(provider)}/metadata`);
  return data.data ?? data;
}

export async function getConfig(
  campaignId: string,
  flowId?: string,
  provider: FlowOptimizerProvider = 'voluum',
): Promise<FlowOptimizerConfig | null> {
  const url = flowId
    ? `${baseFor(provider)}/${campaignId}?flowId=${encodeURIComponent(flowId)}`
    : `${baseFor(provider)}/${campaignId}`;
  const { data } = await api.get(url);
  return (data.data ?? data) as FlowOptimizerConfig | null;
}

export async function upsertConfig(
  campaignId: string,
  body: Partial<FlowOptimizerConfig> & {
    flowId: string;
    /** Refreshes packageAppliedAt even for the same package id (content was
     *  just synced to the package — clears the "edited" badge). */
    packageApplied?: boolean;
  },
  provider: FlowOptimizerProvider = 'voluum',
): Promise<FlowOptimizerConfig> {
  const { data } = await api.put(`${baseFor(provider)}/${campaignId}`, body);
  return (data.data ?? data) as FlowOptimizerConfig;
}

export type PreviewOptimizerDraft = Partial<FlowOptimizerConfig> & {
  /**
   * Optional Voluum flow draft. When present the backend walks this draft
   * instead of fetching the live flow, so the live preview reflects unsaved
   * Standard tab edits (toggled paths, weight tweaks).
   */
  flowOverride?: VoluumFlowUpdate;
};

/**
 * A preview/apply fetches every stats window the config needs (five Binom reports per
 * window on big campaigns); the shared 45 s axios timeout cut cold runs off as
 * "Preview blocked: timeout", so these two calls get their own budget.
 */
const OPTIMIZER_RUN_TIMEOUT_MS = 4 * 60 * 1000;

export async function previewOptimizer(
  campaignId: string,
  provider: FlowOptimizerProvider = 'voluum',
  draft?: PreviewOptimizerDraft,
): Promise<FlowOptimizerPreviewResult> {
  const { data } = await api.post(
    `${baseFor(provider)}/${campaignId}/preview`,
    draft ?? {},
    { timeout: OPTIMIZER_RUN_TIMEOUT_MS },
  );
  return (data.data ?? data) as FlowOptimizerPreviewResult;
}

export async function applyOptimizer(
  campaignId: string,
  provider: FlowOptimizerProvider = 'voluum',
): Promise<FlowOptimizerPreviewResult> {
  const { data } = await api.post(
    `${baseFor(provider)}/${campaignId}/apply`,
    {},
    { timeout: OPTIMIZER_RUN_TIMEOUT_MS },
  );
  return (data.data ?? data) as FlowOptimizerPreviewResult;
}

export async function setKillSwitch(
  campaignId: string,
  killSwitch: boolean,
  provider: FlowOptimizerProvider = 'voluum',
): Promise<FlowOptimizerConfig> {
  const { data } = await api.post(
    `${baseFor(provider)}/${campaignId}/kill-switch`,
    { killSwitch },
  );
  return (data.data ?? data) as FlowOptimizerConfig;
}

export async function listConfigs(
  provider: FlowOptimizerProvider = 'voluum',
): Promise<FlowOptimizerConfigListItem[]> {
  const { data } = await api.get(`${baseFor(provider)}/configs`);
  return (data.data ?? data) as FlowOptimizerConfigListItem[];
}

export async function deleteConfig(
  campaignId: string,
  provider: FlowOptimizerProvider = 'voluum',
): Promise<void> {
  await api.delete(`${baseFor(provider)}/${campaignId}`);
}

export async function setEnabled(
  campaignId: string,
  enabled: boolean,
  provider: FlowOptimizerProvider = 'voluum',
): Promise<FlowOptimizerConfig> {
  const { data } = await api.post(
    `${baseFor(provider)}/${campaignId}/enabled`,
    { enabled },
  );
  return (data.data ?? data) as FlowOptimizerConfig;
}

export async function getSchedulerStatus(
  provider: FlowOptimizerProvider = 'voluum',
): Promise<FlowOptimizerSchedulerStatus> {
  const { data } = await api.get(`${baseFor(provider)}/scheduler/status`);
  return (data.data ?? data) as FlowOptimizerSchedulerStatus;
}

// --- Flow snapshots (under /{provider}/flows/:flowId/snapshots) ---

import type { FlowSnapshotSummary } from '@/features/optimizer-center/optimizer-center.types';

/** List a flow's snapshots, most-recent first (every tracker shares the shape). */
export async function listFlowSnapshots(
  flowId: string,
  provider: FlowOptimizerProvider = 'voluum',
): Promise<{ snapshots: FlowSnapshotSummary[]; count: number }> {
  const { data } = await api.get(`/${provider}/flows/${flowId}/snapshots`);
  return (data.data ?? data) as {
    snapshots: FlowSnapshotSummary[];
    count: number;
  };
}

/** Restore a snapshot — PUTs its stored flow/rotation JSON back to the provider. */
export async function restoreFlowSnapshot(
  flowId: string,
  snapshotId: string,
  provider: FlowOptimizerProvider = 'voluum',
  /** Also sync the bank/queue lifecycle to the restored flow (recommended). */
  syncQueue = true,
): Promise<FlowSnapshotSummary> {
  const { data } = await api.post(
    `/${provider}/flows/${flowId}/snapshots/${snapshotId}/restore`,
    { syncQueue },
  );
  return (data.data ?? data) as FlowSnapshotSummary;
}
